import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase, safeQuery } from '../lib/supabase'
import { briefHasActivity } from '../lib/designStudio'

// ─── Design Studio data for one property ────────────────────────────────────
// Client-side against Supabase with RLS — no api/ route (vercel.json is at
// the 50-function cap; same pattern as the knowledge review panel). The
// Mac-mini worker does the actual generation; while any take is queued or
// generating this hook polls so the grid fills in as images land.

const POLL_MS = 5000

export function useDesignBriefs(propertyId, { all = false } = {}) {
  const [briefs, setBriefs] = useState([])
  const [loading, setLoading] = useState(true)
  const pollRef = useRef(null)

  const fetchAll = useCallback(async () => {
    if (!propertyId && !all) return
    // The global Studio view joins the property for its chip; the per-property
    // tab already knows where it is.
    const embeds = '*, takes:design_takes(*, comments:design_take_comments(*)), refinements:design_refinements(*)'
    let q = supabase
      .from('design_briefs')
      .select(all ? `${embeds}, property:properties(id, title)` : embeds)
      .neq('status', 'archived')
      .order('created_at', { ascending: false })
    q = all ? q.limit(100) : q.eq('property_id', propertyId)
    const { data, error } = await safeQuery(q)
    if (!error) {
      const rows = (data || []).map(b => ({
        ...b,
        takes: (b.takes || [])
          .sort((a, z) => (a.created_at || '').localeCompare(z.created_at || ''))
          .map(t => ({
            ...t,
            comments: (t.comments || []).sort((a, z) => (a.created_at || '').localeCompare(z.created_at || '')),
          })),
        refinements: (b.refinements || []).sort((a, z) => (z.created_at || '').localeCompare(a.created_at || '')),
      }))
      setBriefs(rows)
    }
    setLoading(false)
    return data
  }, [propertyId, all])

  useEffect(() => { setLoading(true); fetchAll() }, [fetchAll])

  // Poll only while the worker owes us something — generation, an
  // assessment, or a refinement pass.
  const anyActive = briefs.some(briefHasActivity)
  useEffect(() => {
    if (!anyActive) return
    pollRef.current = setInterval(fetchAll, POLL_MS)
    return () => clearInterval(pollRef.current)
  }, [anyActive, fetchAll])

  return { briefs, loading, refetch: fetchAll }
}

// ─── One brief, whole — the brief's own page ────────────────────────────────

export function useDesignBrief(briefId) {
  const [brief, setBrief] = useState(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const pollRef = useRef(null)

  const fetchOne = useCallback(async () => {
    if (!briefId) return
    const { data, error } = await safeQuery(
      supabase
        .from('design_briefs')
        .select('*, takes:design_takes(*, comments:design_take_comments(*)), refinements:design_refinements(*), property:properties(id, title)')
        .eq('id', briefId)
        .maybeSingle()
    )
    if (!error) {
      if (!data) setNotFound(true)
      else setBrief({
        ...data,
        takes: (data.takes || [])
          .sort((a, z) => (a.created_at || '').localeCompare(z.created_at || ''))
          .map(t => ({
            ...t,
            comments: (t.comments || []).sort((a, z) => (a.created_at || '').localeCompare(z.created_at || '')),
          })),
        refinements: (data.refinements || []).sort((a, z) => (z.created_at || '').localeCompare(a.created_at || '')),
      })
    }
    setLoading(false)
  }, [briefId])

  useEffect(() => { setLoading(true); setNotFound(false); fetchOne() }, [fetchOne])

  const active = brief ? briefHasActivity(brief) : false
  useEffect(() => {
    if (!active) return
    pollRef.current = setInterval(fetchOne, POLL_MS)
    return () => clearInterval(pollRef.current)
  }, [active, fetchOne])

  return { brief, loading, notFound, refetch: fetchOne }
}

// ─── CRUD ───────────────────────────────────────────────────────────────────

// One brief + one queued take per (provider × variation). The worker claims
// the queued rows; nothing else writes design_takes.
export async function createBriefWithTakes({
  property, project, subjectName, title, briefText, feedbackContext, prompt, providers, variations, userId,
}) {
  const companyId = property?.company_id || 'chatrone_us'
  const { data: brief, error } = await supabase
    .from('design_briefs')
    .insert({
      company_id: companyId,
      property_id: property?.id || null,
      project_id: project?.id || null,
      subject_name: subjectName?.trim() || null,
      title: title?.trim() || subjectName?.trim() || 'Design brief',
      brief_text: briefText?.trim() || '',
      feedback_context: feedbackContext?.trim() || null,
      created_by: userId || null,
    })
    .select()
    .single()
  if (error) throw error

  await insertTakes({ brief, prompt, providers, variations, userId, companyId })
  return brief
}

export async function insertTakes({ brief, prompt, providers, variations = 1, userId, companyId }) {
  const rows = []
  for (const provider of providers) {
    for (let i = 0; i < variations; i++) {
      rows.push({
        company_id: companyId || brief.company_id || 'chatrone_us',
        brief_id: brief.id,
        provider,
        prompt,
        status: 'queued',
        requested_by: userId || null,
      })
    }
  }
  if (!rows.length) return []
  const { data, error } = await supabase.from('design_takes').insert(rows).select()
  if (error) throw error
  return data
}

export async function retryTake(takeId) {
  const { error } = await supabase
    .from('design_takes')
    .update({ status: 'queued', error: null, updated_at: new Date().toISOString() })
    .eq('id', takeId)
  if (error) throw error
}

export async function setTakeReference(takeId, isReference, note) {
  const { error } = await supabase
    .from('design_takes')
    .update({
      is_reference: isReference,
      reference_note: note ?? null,
      updated_at: new Date().toISOString(),
    })
    .eq('id', takeId)
  if (error) throw error
}

// Hard delete per the standing privacy preference (purge over soft-hide).
// The storage object is removed alongside the row.
export async function deleteTake(take) {
  if (take.storage_path) {
    await supabase.storage.from('design-takes').remove([take.storage_path])
  }
  const { error } = await supabase.from('design_takes').delete().eq('id', take.id)
  if (error) throw error
}

// ─── Comments on a take ─────────────────────────────────────────────────────
// Human judgment on individual images. The worker's refinement pass reads
// these back into Claude's steering prompt (the JUDGMENT-LEDGER read-back).

export async function addTakeComment({ take, body, userId, authorName }) {
  const { data, error } = await supabase
    .from('design_take_comments')
    .insert({
      company_id: take.company_id || 'chatrone_us',
      take_id: take.id,
      author_id: userId || null,
      author_name: authorName || null,
      body: body.trim(),
    })
    .select()
    .single()
  if (error) throw error
  return data
}

export async function deleteTakeComment(commentId) {
  const { error } = await supabase.from('design_take_comments').delete().eq('id', commentId)
  if (error) throw error
}

// ─── Refinements: steer the rest of the grid from the picks ─────────────────
// One queued row per ask; the worker studies the starred takes (plus every
// comment) and writes a revised prompt per off-direction take. Suggestions
// stay advisory — generating from one is a separate human click below.

export async function requestRefinement({ brief, referenceTakeIds, guidance, userId }) {
  const { data, error } = await supabase
    .from('design_refinements')
    .insert({
      company_id: brief.company_id || 'chatrone_us',
      brief_id: brief.id,
      reference_take_ids: referenceTakeIds,
      guidance: guidance?.trim() || null,
      status: 'queued',
      requested_by: userId || null,
    })
    .select()
    .single()
  if (error) throw error
  return data
}

// Queue one new take from a refinement suggestion (possibly hand-edited).
// refinedFrom links the new take back to the one it steers away from.
export async function queueRefinedTake({ brief, provider, prompt, refinedFrom, userId }) {
  const { data, error } = await supabase
    .from('design_takes')
    .insert({
      company_id: brief.company_id || 'chatrone_us',
      brief_id: brief.id,
      provider,
      prompt,
      status: 'queued',
      refined_from: refinedFrom || null,
      requested_by: userId || null,
    })
    .select()
    .single()
  if (error) throw error
  return data
}

// ─── The decision ladder ────────────────────────────────────────────────────

export async function setTakeStatus(takeId, status) {
  const { error } = await supabase
    .from('design_takes')
    .update({ take_status: status, updated_at: new Date().toISOString() })
    .eq('id', takeId)
  if (error) throw error
}

// ─── Canon: the property-level record of the decided look ───────────────────
// Promotion is always a human click. The entry snapshots the image so canon
// survives its take being deleted; retiring keeps the row (judgment history).

export async function fetchCanon(propertyId, subject = null) {
  if (!propertyId) return []
  let q = supabase
    .from('design_canon')
    .select('*')
    .eq('property_id', propertyId)
    .is('retired_at', null)
    .order('created_at', { ascending: false })
  if (subject?.trim()) q = q.ilike('subject', subject.trim())
  const { data, error } = await q
  if (error) throw error
  return data || []
}

export async function promoteTakeToCanon({ take, brief, subject, note, userId }) {
  if (!brief.property_id) throw new Error('Canon lives on a property — attach this brief to one first')
  const { data, error } = await supabase
    .from('design_canon')
    .insert({
      company_id: brief.company_id || 'chatrone_us',
      property_id: brief.property_id,
      subject: (subject || brief.subject_name || 'Visual style').trim(),
      take_id: take.id,
      storage_path: take.storage_path,
      image_url: take.image_url,
      note: note?.trim() || null,
      established_by: userId || null,
    })
    .select()
    .single()
  if (error) throw error
  await setTakeStatus(take.id, 'canon')
  return data
}

export async function retireCanonEntry(entry) {
  const { error } = await supabase
    .from('design_canon')
    .update({ retired_at: new Date().toISOString(), updated_at: new Date().toISOString() })
    .eq('id', entry.id)
  if (error) throw error
  // The take steps back down the ladder; it was approved before it was canon.
  if (entry.take_id) await setTakeStatus(entry.take_id, 'approved').catch(() => {})
}

export async function updateBriefText(briefId, briefText) {
  const { error } = await supabase
    .from('design_briefs')
    .update({ brief_text: briefText, updated_at: new Date().toISOString() })
    .eq('id', briefId)
  if (error) throw error
}

// Which Inspiration references steer this brief's next attempt. Per-brief and
// explicit: attaching every reference on the property would silently change
// every future generation, and different briefs want different reference.
export async function setBriefInspiration(briefId, referenceIds) {
  const { error } = await supabase
    .from('design_briefs')
    .update({ inspiration_ids: referenceIds, updated_at: new Date().toISOString() })
    .eq('id', briefId)
  if (error) throw error
}

// The Inspiration board rows a brief can draw on: this property's, plus the
// unattached general ones — the same scoping the board itself uses.
export async function fetchInspirationOptions(propertyId) {
  let q = supabase
    .from('design_references')
    .select('id, storage_path, image_url, note, tags')
    .order('created_at', { ascending: false })
    .limit(120)
  q = propertyId
    ? q.or(`property_id.eq.${propertyId},property_id.is.null`)
    : q.is('property_id', null)
  const { data, error } = await q
  if (error) throw error
  return data || []
}

// Re-run Claude's prompt-fit assessment on one take.
export async function reassessTake(takeId) {
  const { error } = await supabase
    .from('design_takes')
    .update({ assessment_status: 'pending', updated_at: new Date().toISOString() })
    .eq('id', takeId)
  if (error) throw error
}

export async function archiveBrief(briefId) {
  const { error } = await supabase
    .from('design_briefs')
    .update({ status: 'archived', updated_at: new Date().toISOString() })
    .eq('id', briefId)
  if (error) throw error
}
